/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/** @sfdc-extension-file SFDC_EXT_SF_PAYMENTS */

import applePayLogo from '/images/apple-pay-logo.svg';
import googlePayLogo from '/images/google-pay-logo.svg';

export type WalletMethod = 'applepay' | 'googlepay';

export function WalletButtonPreview({
    methods,
    layout = 'vertical',
    testId = 'cart-wallet-preview',
}: {
    methods: WalletMethod[];
    layout?: 'horizontal' | 'vertical';
    testId?: string;
}) {
    // RGBARRY-CUSTOMIZATION: let a single retained wallet fill horizontal previews after Apple Pay filtering.
    const horizontalColumns = methods.length > 1 ? 'grid-cols-2' : 'grid-cols-1';
    // END RGBARRY-CUSTOMIZATION

    return (
        <div
            className={`grid gap-2 ${layout === 'horizontal' ? horizontalColumns : 'grid-cols-1'}`}
            aria-hidden="true"
            data-testid={testId}>
            {methods.map((method) => (
                <div
                    key={method}
                    data-testid={`${testId}-${method}`}
                    className="pointer-events-none flex h-11 min-w-0 w-full select-none items-center justify-center bg-foreground">
                    <img
                        src={method === 'applepay' ? applePayLogo : googlePayLogo}
                        alt=""
                        aria-hidden="true"
                        width={method === 'applepay' ? 48 : 41}
                        height="16"
                        className="h-4 w-auto object-contain"
                        style={method === 'applepay' ? { filter: 'brightness(0) invert(1)' } : undefined}
                    />
                </div>
            ))}
        </div>
    );
}
